import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import API from './api';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Client-side validation
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await API.post('/auth/reset-password', {
        token,
        newPassword
      });
      
      setSuccess('Password reset successfully! You can now login with your new password.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.error || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0e0e1a, #1a1a2e)',
        padding: '20px'
      }}>
        <div style={{
          background: '#12121e',
          padding: '40px',
          borderRadius: '12px',
          border: '1px solid #1c1c32',
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#ffffff', marginBottom: '20px' }}>Invalid Reset Link</h2>
          <p style={{ color: '#8a8aa5', marginBottom: '30px' }}>
            This password reset link is invalid or has expired.
          </p>
          <Link 
            to="/login"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #4a6cff, #6b85ff)',
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600'
            }}
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0e0e1a, #1a1a2e)',
      padding: '20px'
    }}>
      <div style={{
        background: '#12121e',
        padding: '40px',
        borderRadius: '12px',
        border: '1px solid #1c1c32',
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
      }}>
        <h2 style={{ 
          color: '#ffffff', 
          textAlign: 'center', 
          marginBottom: '30px',
          fontSize: '28px',
          fontWeight: '800'
        }}>
          Reset Password
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '15px',
                border: '1px solid #1c1c32',
                borderRadius: '8px',
                background: '#0c0c14',
                color: '#ffffff',
                fontSize: '16px',
                outline: 'none'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '15px',
                border: '1px solid #1c1c32',
                borderRadius: '8px',
                background: '#0c0c14',
                color: '#ffffff',
                fontSize: '16px',
                outline: 'none'
              }}
            />
          </div>
          
          {error && (
            <div style={{
              background: '#ff3b30',
              color: '#ffffff',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
          
          {success && (
            <div style={{
              background: '#4cd964',
              color: '#ffffff',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              {success}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '15px',
              background: loading ? '#666' : 'linear-gradient(135deg, #4a6cff, #6b85ff)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>
        
        <p style={{ 
          textAlign: 'center', 
          marginTop: '20px', 
          color: '#8a8aa5',
          fontSize: '14px'
        }}>
          Remember your password?{' '}
          <Link 
            to="/login" 
            style={{
              color: '#4a6cff',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}

export default ResetPassword;
